@if (isLoading()) {
  <div class="loading-spinner--container">
    <app-loading-spinner></app-loading-spinner>
  </div>
} @else {
<div id="title--container">
  <h4>Your user profile</h4>
</div>
<hr />
<div id="account--container">
  <div class="card details">
    <div class="header">
      <div class="card-title">Personal Details</div>
      <div class="body1">Update your first and last name</div>
    </div>
    <hr class="card-divider" aria-hidden="true" />
    <div class="content">
      <div class="form--container">
        <form [formGroup]="updateDetailsForm">
          <app-underlined-form-field
            [parentForm]="updateDetailsForm"
            [field]="firstNameField"
          ></app-underlined-form-field>
          <app-underlined-form-field
            [parentForm]="updateDetailsForm"
            [field]="lastNameField"
          ></app-underlined-form-field>
        </form>
      </div>
      <div class="button--container">
        <app-button
          label="Save changes"
          buttonId="update-details-btn"
          (buttonClicked)="onSubmit()"
          [isDisabled]="
            updateDetailsForm.invalid ||
            updateDetailsForm.pristine ||
            updateIsProcessing() ||
            passwordIsProcessing()
          "
          [isProcessing]="updateIsProcessing()"
        ></app-button>
      </div>
    </div>
  </div>
  <div class="card">
    <div class="header">
      <div class="card-title">Update Email Address</div>
      <div class="body1">{{ currentUser.email }}</div>
    </div>
    <hr class="card-divider" aria-hidden="true" />
    <div class="content">
      <div class="form--container">
        <form [formGroup]="updateEmailForm">
          <app-underlined-form-field
            [parentForm]="updateEmailForm"
            [field]="newEmailAddressField"
          ></app-underlined-form-field>
          <app-underlined-form-field
            [parentForm]="updateEmailForm"
            [field]="confirmNewEmailAddressField"
          ></app-underlined-form-field>
        </form>
      </div>
      <div class="email-warning body2">
        <mat-icon aria-hidden="true">info</mat-icon>A verification link will be
        sent to your new address. Your email won't change until you confirm
        this.
      </div>
      <div class="button--container">
        <app-button
          label="Update email"
          buttonId="update-email-btn"
          (buttonClicked)="onSubmitEmail()"
          [isDisabled]="
            updateEmailForm.invalid ||
            updateEmailForm.pristine ||
            emailIsProcessing() ||
            updateIsProcessing() ||
            passwordIsProcessing()
          "
          [isProcessing]="emailIsProcessing()"
        ></app-button>
      </div>
    </div>
  </div>
  <div class="card">
    <div class="header">
      <div class="card-title">Laboratories</div>
      <div class="body1">Your laboratory memberships</div>
    </div>
    <hr class="card-divider" aria-hidden="true" />
    <div class="content">
      @if (!currentUser?.laboratories?.length) {
        <span id="no-labs">You are not assigned to any laboratories.</span>
      }
      @for (lab of currentUser?.laboratories; track lab.laboratoryId) {
        <app-lab-card
          [lab]="lab"
          [role]="currentUser?.role"
          [readonly]="true"
        ></app-lab-card>
      }
    </div>
  </div>
  <div class="card">
    <div class="header">
      <div class="card-title">Update Password</div>
      <div class="body1">
        Choose a strong password that meets all requirements
      </div>
    </div>
    <hr class="card-divider" aria-hidden="true" />
    <div class="content">
      <div class="form--container">
        <form [formGroup]="updatePasswordForm">
          <app-underlined-form-field
            iconName="lock"
            [parentForm]="updatePasswordForm"
            [field]="passwordField"
          ></app-underlined-form-field>
          <app-underlined-form-field
            iconName="lock"
            [parentForm]="updatePasswordForm"
            [field]="newPasswordField"
          ></app-underlined-form-field>
          <app-underlined-form-field
            iconName="lock"
            [parentForm]="updatePasswordForm"
            [field]="confirmNewPasswordField"
          ></app-underlined-form-field>
        </form>
      </div>
      <ul class="validation--container body2">
        <app-validation-row
          validationText="Password must be at least 12 characters long"
          [passesValidation]="passesValidation('length')"
        ></app-validation-row>
        <app-validation-row
          validationText="Password must contain at least one uppercase letter"
          [passesValidation]="passesValidation('uppercaseLetter')"
        ></app-validation-row>
        <app-validation-row
          validationText="Password must contain at least one lowercase letter"
          [passesValidation]="passesValidation('lowercaseLetter')"
        ></app-validation-row>
        <app-validation-row
          validationText="Password must contain at least one number"
          [passesValidation]="passesValidation('number')"
        ></app-validation-row>
        <app-validation-row
          validationText="Password must contain at least one special character (@#$%^&+!=)"
          [passesValidation]="passesValidation('specialCharacter')"
        ></app-validation-row>
      </ul>
      <div class="button--container">
        <app-button
          label="Update password"
          buttonId="pwd-btn"
          (buttonClicked)="onSubmitPassword()"
          [isDisabled]="
            updatePasswordForm.invalid ||
            passwordIsProcessing() ||
            updateIsProcessing() ||
            updatePasswordForm.pristine
          "
          [isProcessing]="passwordIsProcessing()"
        ></app-button>
      </div>
    </div>
  </div>
  @if (currentUser.twoFactorEnabled && twoFactorUserSettings(); as settings) {
    <div class="card">
      <div class="header">
        <div class="card-title">Two-Factor Authentication</div>
        <div class="body1">
          Manage your two-factor authentication settings and preferences
        </div>
      </div>
      <hr class="card-divider" aria-hidden="true" />
      <div class="content">
        <div class="item">
          <div class="item-title">
            <div class="subtitle2">Two-Factor Authentication</div>
            <div class="body1">Required by your organisation.</div>
          </div>
          <div>
            <mat-slide-toggle
              [checked]="settings.orgRequires"
              [disabled]="true"
              color="primary"
              aria-label="Two-factor authentication enabled"
            ></mat-slide-toggle>
          </div>
        </div>
        <div class="item">
          <div class="item-title">
            <div class="subtitle2">Recovery Codes</div>
            <div class="body1">
              Recovery codes are one-time use codes that can be used to sign in
              if you lose access to your authentication app. You can generate
              new recovery codes at any time, but doing so will invalidate any
              previously generated codes.
            </div>
            <div
              class="recovery-codes"
              [class.error]="settings.recoveryCodesRemaining <= 3"
            >
              {{ settings.recoveryCodesRemaining }}/10 recovery codes remaining
            </div>
          </div>
        </div>
        <div class="button--container">
          <app-button
            label="Generate new codes"
            buttonId="recovery-codes-btn"
            (buttonClicked)="generateRecoveryCodes()"
          ></app-button>
        </div>
      </div>
    </div>
  }
  <app-button-card
    cardStyle="warning"
    title="Sign out of all other devices"
    bodyLine1="This will log you out from all other devices, except this one."
    bodyLine2="You can log back in anytime."
    buttonLabel="Sign out"
    buttonId="sign-out-other-devices-btn"
    iconName="logout"
    (buttonClicked)="signOutOtherDevices()"
  ></app-button-card>
  @if (currentUser?.role | roleCheck: superAdminRoles) {
    <app-button-card
      id="delete-account-card"
      cardStyle="negative"
      title="Delete account"
      bodyLine1="Permanently removes your account & all associated data."
      bodyLine2="This action cannot be undone."
      buttonLabel="Delete account"
      buttonId="delete-account-btn"
      iconName="delete"
      (buttonClicked)="openModal('delete-account')"
    ></app-button-card>
  }
</div>
}
<app-modal
  id="error"
  titleText="Error"
  [primaryText]="error()?.message"
  primaryButtonText="close"
  primaryButtonId="close-button"
  modalStyle="error"
  (modalButtonClicked)="modalButtonClicked($event)"
></app-modal>
<app-modal
  id="update-email"
  titleText="Request Sent"
  primaryText="A verification link has been sent to your new email address. Please click the link in that email to confirm your new address."
  primaryButtonText="close"
  primaryButtonId="close-button"
  modalStyle="success"
  (modalButtonClicked)="modalButtonClicked($event)"
></app-modal>
<app-modal
  id="attention"
  titleText="Attention"
  primaryText="Your changes have not been saved. Press cancel to return to the form and save your changes, or continue to discard."
  buttonLayout="double-button"
  primaryButtonText="Continue"
  primaryButtonId="confirm-navigation"
  secondaryButtonText="Cancel"
  secondaryButtonId="cancel-button"
  modalStyle="warning"
  (modalButtonClicked)="modalButtonClicked($event)"
></app-modal>
<app-validation-modal
  id="delete-account"
  titleText="Delete account"
  validationText="delete"
  primaryButtonText="Delete"
  primaryButtonId="primary-button"
  secondaryButtonText="Close"
  secondaryButtonId="secondary-button"
  (modalButtonClicked)="deleteAccount()"
  [error]="error()"
></app-validation-modal>
<app-modal
  id="logout"
  titleText="Logout"
  primaryText="Your account has been deleted. You will be logged out."
  primaryButtonText="close"
  primaryButtonId="close-button"
  modalStyle="success"
  (modalButtonClicked)="modalButtonClicked('logout')"
></app-modal>
<app-modal
  id="success"
  titleText="Success"
  [primaryText]="updateUserSuccessMessage()"
  primaryButtonText="close"
  primaryButtonId="close-button"
  modalStyle="success"
  (modalButtonClicked)="modalButtonClicked($event)"
></app-modal>
<app-recovery-codes-modal
  id="recovery-codes"
  [parentForm]="recoveryCodesForm"
  [passwordField]="recoveryCodesPasswordField"
  [codeField]="recoveryCodesCodeField"
  [showCodes]="showRecoveryCodes()"
  [recoveryCodes]="recoveryCodes()"
  primaryButtonText="Generate"
  primaryButtonId="generate-btn"
  secondaryButtonText="Cancel"
  secondaryButtonId="cancel-btn"
  [isProcessing]="generateRecoveryCodesProcessing()"
  [error]="error()"
  (modalButtonClicked)="onRecoveryCodesModalButtonClicked($event)"
></app-recovery-codes-modal>
